(() => {
  if (window.WamboltUseCasesLoaded) {
    return;
  }

  window.WamboltUseCasesLoaded = true;


  const SECTION_SELECTOR =
    '[data-wambolt-use-cases]';

  const TAB_SELECTOR =
    '[data-use-case-tab]';

  const PANEL_SELECTOR =
    '[data-use-case-panel]';

  const SELECT_SELECTOR =
    '[data-use-case-select]';

  const REDUCED_MOTION_QUERY =
    window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

  const PANEL_ENTER_DURATION =
    280;

  const instances =
    new WeakMap();


  /* =========================================
     ROOT HELPERS
     ========================================= */

  function getSections(
    root = document
  ) {
    const sections = [];

    if (
      root instanceof Element &&
      root.matches(
        SECTION_SELECTOR
      )
    ) {
      sections.push(
        root
      );
    }

    if (
      root.querySelectorAll
    ) {
      sections.push(
        ...root.querySelectorAll(
          SECTION_SELECTOR
        )
      );
    }

    return sections;
  }


  /* =========================================
     CREATE INSTANCE
     ========================================= */

  function createInstance(
    section
  ) {
    if (
      !section ||
      instances.has(
        section
      )
    ) {
      return;
    }


    const tabs =
      Array.from(
        section.querySelectorAll(
          TAB_SELECTOR
        )
      );

    const panels =
      Array.from(
        section.querySelectorAll(
          PANEL_SELECTOR
        )
      );

    const select =
      section.querySelector(
        SELECT_SELECTOR
      );


    if (
      !tabs.length ||
      !panels.length
    ) {
      return;
    }


    const controller =
      new AbortController();

    const {
      signal,
    } =
      controller;


    let panelEnterTimer =
      null;


    /* =========================================
       LOOKUPS
       ========================================= */

    function getTab(
      id
    ) {
      return tabs.find(
        (tab) =>
          tab.dataset
            .useCaseId === id
      );
    }


    function getPanel(
      id
    ) {
      return panels.find(
        (panel) =>
          panel.dataset
            .useCaseId === id
      );
    }


    /* =========================================
       PANEL TRANSITION
       ========================================= */

    function animatePanel(
      panel
    ) {
      if (
        !panel ||
        REDUCED_MOTION_QUERY.matches
      ) {
        return;
      }


      if (
        panelEnterTimer
      ) {
        window.clearTimeout(
          panelEnterTimer
        );

        panelEnterTimer =
          null;
      }


      panel.classList.remove(
        'is-entering'
      );


      /*
       * Force the removal to commit so the
       * animation can restart during repeated
       * use-case changes.
       */
      void panel.offsetWidth;


      panel.classList.add(
        'is-entering'
      );


      panelEnterTimer =
        window.setTimeout(
          () => {
            panel.classList.remove(
              'is-entering'
            );

            panelEnterTimer =
              null;
          },
          PANEL_ENTER_DURATION + 40
        );
    }


    /* =========================================
       ACTIVE STATE
       ========================================= */

    function activate(
      id,
      options = {}
    ) {
      const {
        focus = false,
        animate = true,
      } =
        options;


      const activeTab =
        getTab(
          id
        );

      const activePanel =
        getPanel(
          id
        );


      if (
        !activeTab ||
        !activePanel
      ) {
        return;
      }


      const previousPanel =
        panels.find(
          (panel) =>
            panel.classList.contains(
              'is-active'
            )
        );


      const changed =
        previousPanel !==
        activePanel;


      tabs.forEach(
        (tab) => {
          const isActive =
            tab === activeTab;


          tab.classList.toggle(
            'is-active',
            isActive
          );


          tab.setAttribute(
            'aria-selected',
            isActive
              ? 'true'
              : 'false'
          );


          tab.tabIndex =
            isActive
              ? 0
              : -1;
        }
      );


      panels.forEach(
        (panel) => {
          const isActive =
            panel ===
            activePanel;


          panel.classList.toggle(
            'is-active',
            isActive
          );


          panel.hidden =
            !isActive;


          panel.toggleAttribute(
            'inert',
            !isActive
          );
        }
      );


      if (
        select &&
        select.value !== id
      ) {
        select.value =
          id;
      }


      section.dataset
        .activeUseCase =
        id;


      if (
        changed &&
        animate
      ) {
        animatePanel(
          activePanel
        );
      }


      if (
        focus
      ) {
        activeTab.focus();
      }
    }


    /* =========================================
       KEYBOARD NAVIGATION
       ========================================= */

    function activateByIndex(
      currentIndex,
      direction
    ) {
      const nextIndex =
        (
          currentIndex +
          direction +
          tabs.length
        ) %
        tabs.length;


      const nextTab =
        tabs[
          nextIndex
        ];


      activate(
        nextTab.dataset
          .useCaseId,
        {
          focus: true,
        }
      );
    }


    tabs.forEach(
      (
        tab,
        index
      ) => {

        tab.addEventListener(
          'click',
          () => {
            activate(
              tab.dataset
                .useCaseId
            );
          },
          {
            signal,
          }
        );


        tab.addEventListener(
          'keydown',
          (event) => {

            if (
              event.key ===
              'ArrowDown'
            ) {
              event.preventDefault();


              activateByIndex(
                index,
                1
              );


              return;
            }


            if (
              event.key ===
              'ArrowUp'
            ) {
              event.preventDefault();


              activateByIndex(
                index,
                -1
              );


              return;
            }


            if (
              event.key ===
              'Home'
            ) {
              event.preventDefault();


              activate(
                tabs[0]
                  .dataset
                  .useCaseId,
                {
                  focus: true,
                }
              );


              return;
            }


            if (
              event.key ===
              'End'
            ) {
              event.preventDefault();


              activate(
                tabs[
                  tabs.length - 1
                ]
                  .dataset
                  .useCaseId,
                {
                  focus: true,
                }
              );
            }

          },
          {
            signal,
          }
        );

      }
    );


    /* =========================================
       MOBILE / TABLET SELECT
       ========================================= */

    if (
      select
    ) {
      select.addEventListener(
        'change',
        () => {
          activate(
            select.value
          );
        },
        {
          signal,
        }
      );
    }


    /* =========================================
       INITIAL STATE
       ========================================= */

    const initialId =
      tabs.find(
        (tab) =>
          tab.classList.contains(
            'is-active'
          )
      )?.dataset.useCaseId ||
      tabs[0]?.dataset
        .useCaseId;


    if (
      initialId
    ) {
      activate(
        initialId,
        {
          animate:
            false,
        }
      );
    }


    /* =========================================
       INSTANCE
       ========================================= */

    instances.set(
      section,
      {
        activate,

        destroy() {
          controller.abort();


          if (
            panelEnterTimer
          ) {
            window.clearTimeout(
              panelEnterTimer
            );

            panelEnterTimer =
              null;
          }


          panels.forEach(
            (panel) => {
              panel.classList.remove(
                'is-entering'
              );
            }
          );


          instances.delete(
            section
          );
        },
      }
    );
  }


  /* =========================================
     DESTROY INSTANCE
     ========================================= */

  function destroyInstance(
    section
  ) {
    const instance =
      instances.get(
        section
      );


    if (
      !instance
    ) {
      return;
    }


    instance.destroy();
  }


  /* =========================================
     COMPONENT LIFECYCLE
     ========================================= */

  function initWithin(
    root = document
  ) {
    getSections(
      root
    ).forEach(
      createInstance
    );
  }


  function destroyWithin(
    root = document
  ) {
    getSections(
      root
    ).forEach(
      destroyInstance
    );
  }


  /* =========================================
     INITIAL LOAD
     ========================================= */

  function init() {
    initWithin(
      document
    );
  }


  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once:
          true,
      }
    );
  } else {
    init();
  }


  /* =========================================
     SHOPIFY SECTION LOAD
     ========================================= */

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initWithin(
        event.target
      );
    }
  );


  /* =========================================
     SHOPIFY SECTION UNLOAD
     ========================================= */

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroyWithin(
        event.target
      );
    }
  );


  /* =========================================
     SHOPIFY BLOCK SELECTION
     ========================================= */

  document.addEventListener(
    'shopify:block:select',
    (event) => {

      const target =
        event.target instanceof Element
          ? event.target
          : null;


      const section =
        target?.closest(
          SECTION_SELECTOR
        );


      if (
        !section
      ) {
        return;
      }


      const blockElement =
        target.closest(
          '[data-block-id]'
        );


      const blockId =
        blockElement?.dataset
          .blockId ||
        event.detail?.blockId;


      if (
        !blockId
      ) {
        return;
      }


      const instance =
        instances.get(
          section
        );


      instance?.activate(
        blockId
      );

    }
  );


  /* =========================================
     WAMBOLT / BARBA BEFORE LEAVE
     ========================================= */

  document.addEventListener(
    'wambolt:page:before-leave',
    () => {
      destroyWithin(
        document
      );
    }
  );


  /* =========================================
     WAMBOLT / BARBA PAGE ENTER
     ========================================= */

  document.addEventListener(
    'wambolt:page:enter',
    () => {
      initWithin(
        document
      );
    }
  );

})();